import {
  formatDateForInlineEdit,
  parseDateForInlineEdit,
  sameCalendarDay,
  toCalendarDayString,
  GANTT_DATE_EDIT_FORMAT_PLACEHOLDER,
} from './ganttDateFormat';

type DateEditorState = {
  text: HTMLInputElement;
  native: HTMLInputElement;
  originalDay: string | null;
  keydownHandler?: (e: KeyboardEvent) => void;
};

type DateEditorPlaceholder = HTMLElement & {
  __ganttDateEditor?: DateEditorState;
};

/**
 * Calendar day the Start/End editor should show — prefer store inclusive start/finish
 * (same source as the idle cell template) over possibly desynced gantt dates.
 */
export function resolveDateEditorSeedDay(params: {
  columnName: 'start_date' | 'end_date';
  storeStart?: string | Date | null;
  storeFinish?: string | Date | null;
  ganttStart?: string | Date | null;
  ganttEnd?: string | Date | null;
}): string | null {
  if (params.columnName === 'end_date') {
    return (
      toCalendarDayString(params.storeFinish ?? null) ??
      toCalendarDayString(params.ganttEnd ?? null)
    );
  }
  return (
    toCalendarDayString(params.storeStart ?? null) ??
    toCalendarDayString(params.ganttStart ?? null)
  );
}

/** Apply an inclusive YYYY-MM-DD (or Date) into the typed date editor inputs. */
export function applyInclusiveDayToDateEditor(
  eds: Pick<DateEditorState, 'text' | 'native'> & { originalDay?: string | null },
  inclusiveDay: string | Date | null | undefined,
): void {
  const day = toCalendarDayString(inclusiveDay ?? null);
  const formatted = day ? formatDateForInlineEdit(day) : '';
  eds.text.value = formatted;
  eds.native.value = day ?? '';
  if ('originalDay' in eds) {
    eds.originalDay = day;
  }
}

/**
 * Custom DHTMLX inline date editor:
 * - Text field so users can type DD/MM/YYYY
 * - Calendar button / double-click for picker (not auto-open on single click)
 * - is_changed compares calendar days only (avoids midnight time drift dirtying Save)
 */
export function createGanttTypedDateEditor(_gantt: unknown) {
  return {
    show(_id: string | number, column: { name: string }, _config: unknown, placeholder: HTMLElement) {
      const root = placeholder as DateEditorPlaceholder;
      root.innerHTML =
        `<div class="gantt-date-editor" role="cell">` +
        `<input type="text" class="gantt-date-editor-text" name="${column.name}" ` +
        `title="${column.name}" placeholder="${GANTT_DATE_EDIT_FORMAT_PLACEHOLDER}" ` +
        `inputmode="numeric" autocomplete="off" spellcheck="false" />` +
        `<button type="button" class="gantt-date-editor-cal" tabindex="-1" ` +
        `aria-label="Open calendar" title="Open calendar"></button>` +
        `<input type="date" class="gantt-date-editor-native" tabindex="-1" aria-hidden="true" />` +
        `</div>`;

      const text = root.querySelector('.gantt-date-editor-text') as HTMLInputElement;
      const native = root.querySelector('.gantt-date-editor-native') as HTMLInputElement;
      const cal = root.querySelector('.gantt-date-editor-cal') as HTMLButtonElement;

      const syncNativeFromText = () => {
        const parsed = parseDateForInlineEdit(text.value);
        const day = parsed ? toCalendarDayString(parsed) : null;
        native.value = day ?? '';
      };

      const syncTextFromNative = () => {
        if (!native.value) return;
        const parsed = parseDateForInlineEdit(native.value);
        if (parsed) text.value = formatDateForInlineEdit(parsed);
      };

      const openPicker = (e?: Event) => {
        e?.preventDefault();
        e?.stopPropagation();
        syncNativeFromText();
        try {
          if (typeof native.showPicker === 'function') {
            native.showPicker();
          } else {
            native.focus();
            native.click();
          }
        } catch {
          native.focus();
        }
      };

      cal.addEventListener('mousedown', openPicker);
      cal.addEventListener('click', openPicker);

      // Clicking the text field focuses for typing; double-click opens the picker.
      text.addEventListener('dblclick', openPicker);
      native.addEventListener('change', () => {
        syncTextFromNative();
        text.dispatchEvent(new Event('input', { bubbles: true }));
        text.focus();
      });

      root.__ganttDateEditor = { text, native, originalDay: null };

      // Focus text for typing — do not auto-open the native calendar.
      window.requestAnimationFrame(() => {
        text.focus();
        text.select();
      });
    },

    hide() {
      const root = document.querySelector(
        '.gantt_grid_editor_placeholder .gantt-date-editor',
      ) as DateEditorPlaceholder | null;
      const eds = root?.__ganttDateEditor;
      if (eds?.keydownHandler) {
        eds.text.removeEventListener('keydown', eds.keydownHandler);
        eds.keydownHandler = undefined;
      }
      try {
        eds?.text.blur();
      } catch {
        // ignore
      }
    },

    set_value(
      value: Date | string | null | undefined,
      _id: string | number,
      _column: unknown,
      placeholder: HTMLElement,
    ) {
      const root = placeholder as DateEditorPlaceholder;
      const eds = root.__ganttDateEditor;
      if (!eds) return;
      applyInclusiveDayToDateEditor(eds, value);
    },

    get_value(_id: string | number, _column: unknown, placeholder: HTMLElement): Date | null {
      const root = placeholder as DateEditorPlaceholder;
      const eds = root.__ganttDateEditor;
      if (!eds) return null;
      return parseDateForInlineEdit(eds.text.value.trim() || eds.native.value);
    },

    is_valid(value: Date | null): boolean {
      return !!(value && !Number.isNaN(value.getTime()));
    },

    is_changed(
      value: Date | string | null | undefined,
      _id: string | number,
      _column: unknown,
      placeholder: HTMLElement,
    ): boolean {
      const root = placeholder as DateEditorPlaceholder;
      const eds = root.__ganttDateEditor;
      const next = parseDateForInlineEdit(
        eds?.text.value.trim() || eds?.native.value || '',
      );
      if (!eds) {
        return !sameCalendarDay(value, next);
      }
      // Prefer original day captured at set_value (avoids Date time noise).
      const original = eds.originalDay;
      const nextDay = toCalendarDayString(next);
      if (!nextDay) return false;
      if (original) return original !== nextDay;
      return !sameCalendarDay(value, next);
    },

    get_input(placeholder: HTMLElement): HTMLInputElement | null {
      const root = placeholder as DateEditorPlaceholder;
      return root.__ganttDateEditor?.text ?? null;
    },
  };
}

/** Install the typed date editor onto a live gantt instance (idempotent). */
export function installGanttTypedDateEditor(gantt: {
  config?: { editor_types?: Record<string, unknown> };
}): void {
  if (!gantt?.config) return;
  if (!gantt.config.editor_types) {
    gantt.config.editor_types = {};
  }
  gantt.config.editor_types.date = createGanttTypedDateEditor(gantt);
}
